/**
 * <ConfirmDialog open title confirmLabel onConfirm onCancel error?> — modal
 * confirmation. onConfirm may return a promise; while it is pending both
 * buttons are disabled and Escape does nothing. The caller decides when to
 * close (onCancel, or by unmounting/`open=false` after success).
 */
import React, { useEffect, useId, useState } from 'react';
import Button from './Button';
import Card from './Card';

export default function ConfirmDialog({ open, title, confirmLabel, onConfirm, onCancel, error }) {
  const [pending, setPending] = useState(false);
  const titleId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !pending) onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, pending, onCancel]);

  if (!open) return null;

  const confirm = async () => {
    setPending(true);
    try {
      await onConfirm();
    } catch (e) {
      // The caller reports failures through `error`.
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40">
      <Card
        variant="lifted"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="p-6 w-[min(92vw,420px)] flex flex-col gap-4"
      >
        <h2 id={titleId} className="font-display text-xl font-semibold">{title}</h2>
        {error && <p role="alert" className="text-sm text-status-failed">{error}</p>}
        <div className="flex gap-2 justify-end">
          <Button variant="secondary" disabled={pending} onClick={onCancel}>Cancel</Button>
          <Button variant="danger" disabled={pending} onClick={confirm}>{confirmLabel}</Button>
        </div>
      </Card>
    </div>
  );
}
